/**
 * Клиентский API-слой для работы с заявками на регистрацию.
 * Использует fetch для отправки запросов на мини-бэкенд.
 */

const API_BASE = '/api/registrations';

// --- Типы ---

export interface RegistrationPayload {
  competence: string;
  fullName: string;
  birthDate: string;
  email: string;
  phone: string;
  city: string;
  institution: string;
  agreeTerms: boolean;
  portfolioLinks?: string[];
}

export interface RegistrationResponse {
  success: boolean;
  id: string;
  message: string;
}

export interface ApplicationRecord {
  id: string;
  competence: string;
  fullName: string;
  birthDate: string;
  email: string;
  phone: string;
  city: string;
  institution: string;
  agreeTerms: boolean;
  createdAt: string;
  status: 'new' | 'reviewing' | 'accepted' | 'rejected';
  portfolioLinks?: string[];
}

export interface ListResponse {
  items: ApplicationRecord[];
  total: number;
  page: number;
  limit: number;
}

export interface ApiError {
  error: string;
  details?: string[];
  message?: string;
}

// --- Вспомогательные ---

function getAdminToken(): string {
  return sessionStorage.getItem('admin_token') || '';
}

export function setAdminToken(token: string): void {
  sessionStorage.setItem('admin_token', token);
}

export function clearAdminToken(): void {
  sessionStorage.removeItem('admin_token');
}

/**
 * Читает JSON из ответа, не падая на HTML/пустом теле.
 * Если сервер вернул не JSON (например, страницу ошибки nginx),
 * выбрасывает понятное сообщение вместо "JSON.parse: unexpected character".
 */
async function readJson<T>(res: Response): Promise<T> {
  const text = await res.text();

  if (!text) {
    throw new Error(
      res.ok
        ? 'Сервер вернул пустой ответ'
        : `Ошибка сервера: HTTP ${res.status}`
    );
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    const isHtml = /^\s*</.test(text);
    if (isHtml) {
      throw new Error(describeHtmlResponse(res));
    }
    throw new Error(`Некорректный ответ сервера (HTTP ${res.status})`);
  }
}

/** Понятное сообщение, если вместо JSON пришла HTML-страница */
function describeHtmlResponse(res: Response): string {
  switch (res.status) {
    case 405:
      return 'Сервер не принимает запросы к /api/registrations (HTTP 405). Проверьте настройку nginx: запросы должны проксироваться на бэкенд, а не отдаваться как статика.';
    case 404:
      return 'API не найден (HTTP 404). Проверьте настройку nginx и что бэкенд запущен.';
    case 502:
      return 'Бэкенд недоступен (HTTP 502). Проверьте, запущен ли сервис o4ki.';
    case 503:
    case 504:
      return 'Бэкенд не отвечает (HTTP ' + res.status + '). Проверьте сервис o4ki.';
    case 429:
      return 'Слишком много запросов. Попробуйте позже.';
    default:
      return `Сервер вернул HTML вместо JSON (HTTP ${res.status}). Проверьте настройку nginx для /api/registrations.`;
  }
}

// --- API-функции ---

/**
 * Отправить заявку на регистрацию
 */
export async function submitRegistration(payload: RegistrationPayload): Promise<RegistrationResponse> {
  const res = await fetch(API_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const data = await readJson<ApiError & RegistrationResponse>(res);

  if (!res.ok) {
    const errMsg = data.details?.join('; ') || data.message || data.error || 'Неизвестная ошибка';
    throw new Error(errMsg);
  }

  return data as RegistrationResponse;
}

/**
 * Получить список заявок (админ)
 */
export async function fetchApplications(params?: {
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<ListResponse> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.page) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));

  const res = await fetch(`${API_BASE}?${query.toString()}`, {
    headers: { 'x-admin-token': getAdminToken() },
  });

  if (res.status === 401) {
    throw new Error('Требуется авторизация');
  }
  if (!res.ok) {
    const data = await readJson<ApiError>(res);
    throw new Error(data.error || 'Ошибка загрузки заявок');
  }

  return readJson<ListResponse>(res);
}

/**
 * Обновить статус заявки (админ)
 */
export async function updateApplicationStatus(
  id: string,
  status: ApplicationRecord['status']
): Promise<void> {
  const res = await fetch(`${API_BASE}/${id}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'x-admin-token': getAdminToken(),
    },
    body: JSON.stringify({ status }),
  });

  if (!res.ok) {
    const data = await readJson<ApiError>(res);
    throw new Error(data.error || 'Ошибка обновления статуса');
  }
}

/**
 * Удалить заявку (админ)
 */
export async function deleteApplication(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/${id}`, {
    method: 'DELETE',
    headers: { 'x-admin-token': getAdminToken() },
  });

  if (!res.ok) {
    const data = await readJson<ApiError>(res);
    throw new Error(data.error || 'Ошибка удаления');
  }
}

/**
 * Скачать Excel-экспорт заявок (админ)
 */
export async function downloadExcelExport(): Promise<void> {
  const res = await fetch(`${API_BASE}/export.xlsx`, {
    headers: { 'x-admin-token': getAdminToken() },
  });

  if (!res.ok) {
    const data = await readJson<ApiError>(res);
    throw new Error(data.error || 'Ошибка экспорта');
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `registrations_${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Проверить, валиден ли токен администратора
 */
export async function verifyAdminToken(token: string): Promise<boolean> {
  const res = await fetch(`${API_BASE}?limit=1`, {
    headers: { 'x-admin-token': token },
  });
  if (res.status === 401) return false;

  // readJson упадёт, если вместо API пришла HTML-страница (SPA-fallback),
  // поэтому неверно настроенный nginx не даст войти с любым токеном.
  const data = await readJson<ApiError & { items?: unknown }>(res);

  if (!res.ok) {
    throw new Error(data.error || 'Ошибка проверки токена');
  }
  if (!Array.isArray(data.items)) {
    throw new Error('API вернул неожиданный ответ. Проверьте настройку nginx для /api/registrations.');
  }
  return true;
}

/**
 * Проверка авторизации админа
 */
export function isAdminAuthenticated(): boolean {
  return !!getAdminToken();
}
